import React, { useState, useEffect, useCallback } from 'react';
import { TravelAbilityWordmark } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  Download, 
  Users, 
  CheckCircle2, 
  Target, 
  TrendingUp, 
  Trophy,
  HelpCircle,
  RefreshCw,
  ArrowLeft,
  Loader2,
  FileSpreadsheet,
  HandHeart,
  Bell,
  BellDot,
  Eye,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Mail
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const profileLabels = {
  'awakening': { label: 'The Awakening', color: '#3B82F6', icon: Target },
  'momentum': { label: 'Building Momentum', color: '#10B981', icon: TrendingUp },
  'advanced': { label: 'Advanced/Leading', color: '#FBBF24', icon: Trophy },
  'uncertain': { label: 'Uncertain/Blocked', color: '#9CA3AF', icon: HelpCircle }
};

const commitmentLabels = {
  'discovery': 'Schedule Discovery Call',
  'inventory': 'Inventory Assets',
  'landing': 'Build Landing Page',
  'present': 'Present to Leadership',
  'survey': 'Survey Partners',
  'other': 'Other'
};

const questionLabels = {
  1: "Accessibility Commitment Level",
  2: "Bandwidth & Budget",
  3: "Role / Title"
};

const NotificationCard = ({ notification, onMarkRead }) => {
  const [expanded, setExpanded] = useState(false);
  const isAssessment = notification.type === 'assessment_completed';
  const profileInfo = profileLabels[notification.profile || notification.profile_id] || null;
  const timeAgo = getTimeAgo(notification.created_at);

  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all ${
        notification.read 
          ? 'border-[#333333] bg-[#1A1A1A]' 
          : 'border-[#FBBF24]/50 bg-[#FBBF24]/5'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div 
            className="p-2 rounded-lg mt-0.5"
            style={{ 
              backgroundColor: isAssessment 
                ? (profileInfo?.color || '#3B82F6') + '20' 
                : '#10B98120' 
            }}
          >
            {isAssessment ? (
              <ClipboardList className="w-5 h-5" style={{ color: profileInfo?.color || '#3B82F6' }} />
            ) : (
              <HandHeart className="w-5 h-5 text-[#10B981]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-[#F3F4F6]">{notification.lead_name}</span>
              {!notification.read && (
                <span className="px-2 py-0.5 rounded-full bg-[#FBBF24]/20 text-[#FBBF24] text-xs font-semibold">
                  NEW
                </span>
              )}
            </div>
            <p className="text-sm text-[#9CA3AF]">
              {notification.lead_organization} &middot; {notification.lead_email}
            </p>
            <p className="text-sm mt-1">
              {isAssessment ? (
                <>
                  <span className="text-[#9CA3AF]">Completed assessment — Profile: </span>
                  {profileInfo && (
                    <span 
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${profileInfo.color}20`, color: profileInfo.color }}
                    >
                      {profileInfo.label}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-[#9CA3AF]">Committed to: </span>
                  <span className="text-[#10B981] font-semibold">
                    {commitmentLabels[notification.commitment] || notification.commitment}
                    {notification.commitment_other && ` — ${notification.commitment_other}`}
                  </span>
                </>
              )}
            </p>
            <p className="text-xs text-[#6B7280] mt-1">{timeAgo}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!notification.read && (
            <button
              onClick={() => onMarkRead(notification.id)}
              className="p-2 rounded-lg hover:bg-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
              title="Mark as read"
              data-testid={`mark-read-${notification.id}`}
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          {isAssessment && notification.answers && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 rounded-lg hover:bg-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
              data-testid={`expand-${notification.id}`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Expanded answers detail */}
      {expanded && notification.answers && (
        <div className="mt-4 pt-4 border-t border-[#333333]">
          <h4 className="text-sm font-semibold text-[#FBBF24] mb-3">Full Assessment Answers</h4>
          <div className="space-y-3">
            {Object.entries(notification.answers).map(([qId, answer]) => {
              const qLabel = questionLabels[qId] || `Question ${qId}`;
              let answerDisplay;

              if (answer?.text) {
                answerDisplay = <span className="text-[#F3F4F6] italic">"{answer.text}"</span>;
              } else if (answer?.compound) {
                answerDisplay = (
                  <span className="text-[#F3F4F6]">
                    {Object.entries(answer.compound).map(([key, val]) => `${key}: ${val?.text || val?.id || ''}`).join(' | ')}
                  </span>
                );
              } else if (answer?.selections) {
                answerDisplay = <span className="text-[#F3F4F6]">{answer.selections.join(', ')}</span>;
              } else if (answer?.id) {
                // Single select
                answerDisplay = (
                  <span className="text-[#F3F4F6]">
                    {answer.id}) {answer.text?.substring(0, 80)}{answer.text?.length > 80 ? '...' : ''}
                  </span>
                );
              } else if (typeof answer === 'object' && !answer?.id) {
                // Inventory (Q6)
                const items = Object.entries(answer)
                  .filter(([, v]) => v?.checked)
                  .map(([key, v]) => v.value ? `${key}: ${v.value}` : key);
                answerDisplay = <span className="text-[#F3F4F6]">{items.join(', ') || 'None selected'}</span>;
              } else {
                answerDisplay = <span className="text-[#6B7280]">—</span>;
              }

              return (
                <div key={qId} className="flex flex-col gap-1 p-3 bg-[#222222] rounded-lg">
                  <span className="text-xs text-[#9CA3AF] font-semibold">Q{qId}: {qLabel}</span>
                  <div className="text-sm">{answerDisplay}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

function getTimeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const AdminDashboard = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notifications');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [exportRes, notifRes] = await Promise.all([
        axios.get(`${API}/admin/export`),
        axios.get(`${API}/admin/notifications?limit=50`)
      ]);
      setData(exportRes.data);
      setNotifications(notifRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.post(`${API}/admin/notifications/${notificationId}/read`);
      setNotifications(prev => ({
        ...prev,
        unread_count: Math.max(0, prev.unread_count - 1),
        notifications: prev.notifications.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      }));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/read-all`);
      setNotifications(prev => ({
        ...prev,
        unread_count: 0,
        notifications: prev.notifications.map(n => ({ ...n, read: true }))
      }));
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  const formatAnswer = (qId, answer) => {
    if (!answer) return '';
    if (answer?.text) return answer.text;
    if (answer?.selections) return answer.selections.join('; ');
    if (answer?.compound) {
      return Object.entries(answer.compound).map(([key, val]) => `${key}: ${val?.text || val?.id || ''}`).join('; ');
    }
    if (answer?.id) return `${answer.id}) ${answer.text || ''}`;
    if (typeof answer === 'object' && !answer?.id && !answer?.text && !answer?.selections && !answer?.compound) {
      const items = Object.entries(answer)
        .filter(([, v]) => v?.checked)
        .map(([key, v]) => v.value ? `${key}: ${v.value}` : key);
      return items.join('; ') || '';
    }
    return '';
  };

  const exportToCSV = () => {
    if (!data?.data) return;
    const headers = [
      'Name', 'Email', 'Organization', 'Registered At',
      'Assessment Completed', 'Profile', 'Commitment', 'Completed At',
      'Q1: Accessibility Commitment Level',
      'Q2: Bandwidth & Budget',
      'Q3: Role / Title'
    ];
    const rows = data.data.map(item => {
      const answers = item.answers || {};
      return [
        item.name, item.email, item.organization,
        item.registered_at ? new Date(item.registered_at).toLocaleString() : '',
        item.assessment_completed ? 'Yes' : 'No',
        item.profile ? (profileLabels[item.profile]?.label || item.profile) : '',
        item.commitment ? (commitmentLabels[item.commitment] || item.commitment) : '',
        item.assessment_completed_at ? new Date(item.assessment_completed_at).toLocaleString() : '',
        formatAnswer(1, answers['1']),
        formatAnswer(2, answers['2']),
        formatAnswer(3, answers['3'])
      ];
    });
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `destination-2030-results-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `destination-2030-results-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FBBF24] animate-spin mx-auto mb-4" />
          <p className="text-[#9CA3AF]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const unreadCount = notifications?.unread_count || 0;

  return (
    <div className="min-h-screen bg-[#111111]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={onBack}
              variant="outline"
              className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <TravelAbilityWordmark showTagline={false} />
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-[#F3F4F6] mb-2" data-testid="admin-title">
          Assessment Results Dashboard
        </h1>
        <p className="text-[#9CA3AF] mb-8">
          Monitor leads, assessments, and commitments in real-time
        </p>

        {/* Stats Cards */}
        {data?.stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
              <Card className="bg-[#1A1A1A] border-[#333333]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[#3B82F6]/20">
                      <Users className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#F3F4F6]">{data.stats.total_leads}</p>
                      <p className="text-xs text-[#9CA3AF]">Total Leads</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1A1A1A] border-[#333333]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[#10B981]/20">
                      <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#F3F4F6]">{data.stats.assessments_completed}</p>
                      <p className="text-xs text-[#9CA3AF]">Completed ({data.stats.completion_rate})</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1A1A1A] border-[#333333]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-[#FBBF24]/20">
                      <HandHeart className="w-5 h-5 text-[#FBBF24]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#F3F4F6]">
                        {Object.values(data.stats.commitment_distribution || {}).reduce((a, b) => a + b, 0)}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">Commitments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={`border-[#333333] ${unreadCount > 0 ? 'bg-[#FBBF24]/10 border-[#FBBF24]/30' : 'bg-[#1A1A1A]'}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${unreadCount > 0 ? 'bg-[#FBBF24]/20' : 'bg-[#333333]/50'}`}>
                      {unreadCount > 0 ? (
                        <BellDot className="w-5 h-5 text-[#FBBF24]" />
                      ) : (
                        <Bell className="w-5 h-5 text-[#9CA3AF]" />
                      )}
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#F3F4F6]">{unreadCount}</p>
                      <p className="text-xs text-[#9CA3AF]">Unread Alerts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Profile Distribution */}
            {Object.keys(data.stats.profile_distribution || {}).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {Object.entries(data.stats.profile_distribution).map(([profile, count]) => {
                  const info = profileLabels[profile] || { label: profile, color: '#9CA3AF', icon: Target };
                  const Icon = info.icon;
                  return (
                    <Card key={profile} className="bg-[#1A1A1A] border-[#333333]">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${info.color}20` }}>
                            <Icon className="w-5 h-5" style={{ color: info.color }} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-[#F3F4F6]">{count}</p>
                            <p className="text-xs text-[#9CA3AF]">{info.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'notifications'
                ? 'bg-[#FBBF24] text-[#111111]'
                : 'bg-[#1A1A1A] border border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]'
            }`}
            data-testid="tab-notifications"
          >
            <span className="inline-flex items-center gap-2">
              {unreadCount > 0 ? <BellDot className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              Notifications
              {unreadCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === 'notifications' ? 'bg-[#111111] text-[#FBBF24]' : 'bg-[#FBBF24] text-[#111111]'
                }`}>
                  {unreadCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('table')}
            className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'table'
                ? 'bg-[#FBBF24] text-[#111111]'
                : 'bg-[#1A1A1A] border border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]'
            }`}
            data-testid="tab-table"
          >
            <span className="inline-flex items-center gap-2">
              <Users className="w-4 h-4" />
              All Submissions
            </span>
          </button>
        </div>

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card className="bg-[#1A1A1A] border-[#333333] mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-[#F3F4F6] flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Live Activity Feed
                  </CardTitle>
                  <CardDescription className="text-[#9CA3AF] mt-1">
                    Real-time notifications when assessments are completed
                  </CardDescription>
                </div>
                {unreadCount > 0 && (
                  <Button
                    onClick={handleMarkAllRead}
                    variant="outline"
                    size="sm"
                    className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]"
                    data-testid="mark-all-read-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark All Read
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {notifications?.notifications?.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-[#333333] mx-auto mb-4" />
                  <p className="text-[#6B7280]">No notifications yet.</p>
                  <p className="text-[#6B7280] text-sm">Notifications will appear here when users complete assessments.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications?.notifications?.map((notification) => (
                    <NotificationCard
                      key={notification.id}
                      notification={notification}
                      onMarkRead={handleMarkRead}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Table Tab */}
        {activeTab === 'table' && (
          <>
            {/* Export Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Button
                onClick={exportToCSV}
                className="bg-[#10B981] hover:bg-[#059669] text-[#111111] font-bold"
                data-testid="export-csv-btn"
              >
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={exportToJSON}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold"
                data-testid="export-json-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Export JSON
              </Button>
            </div>

            <Card className="bg-[#1A1A1A] border-[#333333]">
              <CardHeader>
                <CardTitle className="text-[#F3F4F6]">All Submissions</CardTitle>
                <CardDescription className="text-[#9CA3AF]">
                  {data?.data?.length || 0} records found
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="results-table">
                    <thead>
                      <tr className="border-b border-[#333333]">
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Name</th>
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Email</th>
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Organization</th>
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Profile</th>
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Commitment</th>
                        <th className="text-left p-4 text-[#9CA3AF] font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.data?.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center p-8 text-[#6B7280]">
                            No submissions yet.
                          </td>
                        </tr>
                      ) : (
                        data?.data?.map((item, index) => {
                          const profileInfo = profileLabels[item.profile] || null;
                          return (
                            <tr 
                              key={item.lead_id || index} 
                              className="border-b border-[#333333]/50 hover:bg-[#222222]"
                            >
                              <td className="p-4 text-[#F3F4F6]">{item.name}</td>
                              <td className="p-4 text-[#9CA3AF]">{item.email}</td>
                              <td className="p-4 text-[#9CA3AF]">{item.organization}</td>
                              <td className="p-4">
                                {profileInfo ? (
                                  <span 
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm"
                                    style={{ backgroundColor: `${profileInfo.color}20`, color: profileInfo.color }}
                                  >
                                    {profileInfo.label}
                                  </span>
                                ) : (
                                  <span className="text-[#6B7280]">Not completed</span>
                                )}
                              </td>
                              <td className="p-4 text-[#9CA3AF] text-sm">
                                {item.commitment ? (commitmentLabels[item.commitment] || item.commitment) : '—'}
                              </td>
                              <td className="p-4 text-[#9CA3AF] text-sm">
                                {item.registered_at ? new Date(item.registered_at).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Future Email Notice */}
        <div className="mt-8 p-4 bg-[#1A1A1A] border border-[#333333] rounded-xl flex items-start gap-3">
          <Mail className="w-5 h-5 text-[#9CA3AF] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-[#9CA3AF]">
              <span className="text-[#F3F4F6] font-semibold">Email notifications:</span> Configure a Resend API key to receive instant email alerts at <span className="text-[#FBBF24]">alison@travelability.net</span> when new assessments are submitted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
